<template>
  <div :class="['p-5', { 'opacity-50': saving }]">
    <div class="flex mb-10">
      <Button @click="$emit('cancel')" theme="secondary" size="sm" class="mr-3">
        &laquo;
      </Button>
      <Text size="xl" align="center" class="weight-500 relative pt-1">
        Creating TLFB Survey in {{ project.name }}...
      </Text>
    </div>
    <div class="pl-10">
      <WeekCountSelect align="left" v-model="weeksCount" class="pb-7" />
      <SubstanceSelectTable
        :otlfbSurveySubstances="otlfbSurveySubstances"
        :substanceOptions="substanceOptions"
        @update-otlfb-survey-substances="updateOtlfbSurveySubstances"
      />
      <Text :class="{ hidden: !error }">{{ errorMessage }}</Text>
      <div class="mt-5">
        <Button
          theme="secondary"
          size="lg"
          @click="openUrlModalOrCreateSurvey"
          :disabled="otlfbSurveySubstances.length === 0"
          :hover="true"
          :showSpinner="saving"
        >
          Create Survey
        </Button>
      </div>
    </div>
  </div>
  <AddInstructionUrlsModal
    v-if="showAddInstructionUrlsModal"
    :otlfbSurveySubstances="otlfbSurveySubstances"
    @cancel="showAddInstructionUrlsModal = false"
    @submit-urls="saveUrlsAndCreateSurvey"
  />
</template>

<script setup>
import { ref, computed, onMounted, watch, inject } from "vue"
import ApiClient, { SubstanceApi, SubstanceModeApi, OtlfbSurveyApi } from "../api"
import Button from "./Button.vue"
import Text from "./Text.vue"
import WeekCountSelect from "./WeekCountSelect.vue"
import SubstanceSelectTable from "./SubstanceSelectTable.vue"
import AddInstructionUrlsModal from "./AddInstructionUrlsModal.vue"
import hasPresetInstructions from "../utils/hasPresetInstructions"

const emit = defineEmits(["cancel", "otlfb-survey-saved"])

const props = defineProps({
  project: {
    type: Object,
    required: true
  },
  redcapProjectInProduction: {
    type: Boolean,
    required: true
  }
})

const user = inject("user")
const saving = ref(false)
const errorMessage = ref("")
const error = computed(() => errorMessage.value !== "")
const recordIdFirstRedcapField = ref(false)
const substanceOptions = ref([])
const customVariablesSaved = ref(false)
const instructionUrls = ref([])
const showAddInstructionUrlsModal = ref(false)

onMounted(async () => { 
  recordIdFirstRedcapField.value = await isRecordIdFirstRedcapField()
  substanceOptions.value = (
    await SubstanceApi.all({ include: ["substanceModes"] })
  ).data.sort((a, b) => a.id - b.id)
})

const otlfbSurvey = ref({
  projectId: props.project.id,
  dayCount: 35
})

const weeksCount = ref(5)
watch(weeksCount, weeks => {
  otlfbSurvey.value.dayCount = weeks * 7
})

const otlfbSurveySubstances = ref([])

function updateOtlfbSurveySubstances(newSurveySubstances) {
  otlfbSurveySubstances.value = newSurveySubstances
}

async function openUrlModalOrCreateSurvey() {
  errorMessage.value = ''
  // if (!user.value.usableByApi) {
  //   errorMessage.value = 'This user cannot create surveys, likely due to username not registered in REDCap project or user not granted API rights.'
  // } else 
  if (includesSubstancesWithoutPresetInstructions.value) {
    showAddInstructionUrlsModal.value = true
  } else {
    await saveAll()
  }
}

async function saveUrlsAndCreateSurvey(urls) {
  instructionUrls.value = urls
  await saveAll()
}

async function saveAll() {
  await saveCustomSurveyVariables()
  if (customVariablesSaved.value) {
    await saveOtlfbSurvey()
  }
}

// "create survey"
async function saveOtlfbSurvey() {
  errorMessage.value = ""
  try {
    if (recordIdFirstRedcapField.value && !props.redcapProjectInProduction) {
      saving.value = true
      const survey = otlfbSurvey.value
      /* Attach survey substances */
      survey.otlfbSurveySubstances = otlfbSurveySubstances.value
      /* Attach Urls */
      survey.otlfbSurveySubstances.forEach((s, i) => {
        survey.otlfbSurveySubstances[i].instructionUrl = instructionUrls.value[i]
      })
      const response = await OtlfbSurveyApi.save(survey)

      if (response.status === 200) {
        emit("otlfb-survey-saved")
      }
    } else {
      if (!recordIdFirstRedcapField.value) {
        errorMessage.value = "record_id field is not first field in REDCap project. First variable in REDCap project must be record_id."
      } else {
        errorMessage.value = "Forms cannot be created in REDCap project when status = In Production."
      }
    }

  } catch (error) {
    errorMessage.value = user.value.usableByApi
      ? "Error creating REDCap survey. Please try again later."
      : "Error creating REDCap survey: Your username must match the username registered in REDCap project."
  }
  saving.value = false
  // }
}

async function isRecordIdFirstRedcapField() {
  const redcapFields = await ApiClient.post(`/export`, {
    content: "fieldnames",
    projectId: props.project.id,
  })
  const fields = redcapFields.data.map((f) => f?.export_field_name)
  return fields?.[0] === 'record_id'
}

async function saveCustomSurveyVariables() {
  errorMessage.value = ""

  const customVariables = otlfbSurveySubstances.value.filter(s =>
    !s.substance.hasOwnProperty("id") || !s.substanceMode.hasOwnProperty("id")
  )

  let savedCount = 0

  for (const customVariable of customVariables) {
    // does the variable name already exist? This would mean only the mode needs to be saved
    const existingVariableResponse = await SubstanceApi.all({ 
      where: { name: customVariable.substance.name }
    })
    const existingVariable = existingVariableResponse?.data?.[0]

    if (existingVariable) {
      // if substance already exists, add only novel modes
      const newModes = [
        {
          substanceId: existingVariable.id,
          name: customVariable.substanceMode.name,
          dataType: customVariable.substanceMode.dataType,
          redcapFieldName: customVariable.substanceMode.redcapFieldName
        }, {
          substanceId: existingVariable.id,
          name: customVariable.substanceMode.name,
          // add the unselected data type to be an option later
          dataType: customVariable.substanceMode.dataType === 'Numeric' ? 'Binary' : 'Numeric',
          redcapFieldName: customVariable.substanceMode.redcapFieldName
        }
      ]
      const responses = []
      for (let i = 0; i < newModes.length; i++) {
        const response = await SubstanceModeApi.save(newModes[i])
        responses.push(response)

        if ((i == 0) && (response.status === 200)) {
          const savedNewVariable = {
            substance: existingVariable,
            substanceId: existingVariable.id,
            substanceMode: response.data,
            substanceModeId: response.data.id
          }
          // replace the incomplete substance in otlfbSurveySubstances with the new version consistent with back end
          const index = otlfbSurveySubstances.value.findIndex(s => s === customVariable)
          otlfbSurveySubstances.value.splice(index, 1, savedNewVariable)
        }
      }
  
      if (responses.every(r => r.status === 200)) {
        savedCount += 1 
      } else {
        errorMessage.value = "Unable to save custom variables."
        break
      }
        
    } else {
      const newVariable = {
        name: customVariable.substance.name,
        substanceModes: [
          {
            name: customVariable.substanceMode.name,
            dataType: customVariable.substanceMode.dataType,
            redcapFieldName: customVariable.substanceMode.redcapFieldName
          },
          {
            name: customVariable.substanceMode.name,
            // add the unselected data type to be an option later
            dataType: customVariable.substanceMode.dataType === 'Numeric' ? 'Binary' : 'Numeric',
            redcapFieldName: customVariable.substanceMode.redcapFieldName
          }
        ]
      }

      if (customVariable.substanceMode.name.toLowerCase() !== 'all types') {
        // add All Types as a future option
        newVariable.substanceModes = [
          ...newVariable.substanceModes,
          {
            name: 'All Types',
            dataType: 'Binary',
            redcapFieldName: customVariable.substanceMode.redcapFieldName
          },
          {
            name: 'All Types',
            dataType: 'Numeric',
            redcapFieldName: customVariable.substanceMode.redcapFieldName
          }
        ]
      }

      // remove already existing modes
      newVariable.substanceModes = newVariable.substanceModes.filter(newMode => {
        return !substanceOptions.value.some(s => 
        s.substanceName === newVariable.name && 
        s.substanceModes.some(mode => 
          mode.name === newMode.name && mode.dataType === newMode.dataType
        )
      )})
      
      const response = await SubstanceApi.save(newVariable)

      if (response.status === 200) {
        const savedNewVariable = {
          substance: response.data,
          substanceId: response.data.id,
          substanceMode: response.data.substanceModes.filter(m => 
            (m.name === customVariable.substanceMode.name) &&
            (m.dataType === customVariable.substanceMode.dataType)
          )[0],
        }
        savedNewVariable.substanceModeId = savedNewVariable.substanceMode.id

        // replace the incomplete otlfbSurveySubstances with the version consistent with back end
        const index = otlfbSurveySubstances.value.findIndex(s => s === customVariable)
        otlfbSurveySubstances.value.splice(index, 1, savedNewVariable)
        savedCount += 1
      } else {
        errorMessage.value = "Unable to save custom variables."
        break
      }
    }
  }
  if (savedCount === customVariables.length) {
    customVariablesSaved.value = true
  }
}

const includesSubstancesWithoutPresetInstructions = computed(() => {
  return otlfbSurveySubstances.value.some((oss) => { return !hasPresetInstructions(oss, oss.substanceMode.dataType) })
})

</script>
